import { Helmet } from "react-helmet-async";
import { useState, type ReactNode, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { V2_CATEGORIES, V2_CATEGORY_LABELS } from "@/v2/categories";

interface Props {
  title: string;
  description?: string;
  canonicalPath?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
  ogType?: "website" | "article";
  children: ReactNode;
}

const SITE = "https://korthet.se";

export default function BaseLayout({
  title,
  description,
  canonicalPath,
  noindex,
  jsonLd,
  ogType = "website",
  children,
}: Props) {
  const canonical = canonicalPath ? `${SITE}${canonicalPath}` : SITE;
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    navigate(`/sok?q=${encodeURIComponent(term)}`);
  };

  return (
    <div className="k2-shell">
      <Helmet>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        {description && <meta property="og:description" content={description} />}
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content={ogType} />
        {noindex && <meta name="robots" content="noindex,nofollow" />}
        {jsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
          </script>
        )}
      </Helmet>

      <header className="k2-header">
        <div className="k2-brand-wrap">
          <Link to="/" className="k2-brand">
            <img src="/favicon.ico" alt="" className="k2-logo" aria-hidden="true" />
            <span className="k2-brand-text">Korthet.se</span>
          </Link>
          <p className="k2-tagline">Mindre brus. Mer koll. Snabbt!</p>
        </div>
        <form className="k2-search" role="search" onSubmit={submitSearch}>
          <label htmlFor="k2-search-input" className="k2-visually-hidden">
            Sök artiklar
          </label>
          <input
            id="k2-search-input"
            type="search"
            name="q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sök…"
            autoComplete="off"
          />
          <button type="submit" aria-label="Sök">Sök</button>
        </form>
        <nav className="k2-nav" aria-label="Kategorier">
          <NavLink to="/" end>
            Senaste
          </NavLink>
          {V2_CATEGORIES.map((cat) => (
            <NavLink key={cat} to={`/${cat}`} data-cat={cat}>
              {V2_CATEGORY_LABELS[cat]}
            </NavLink>
          ))}
          <NavLink to="/trendar" data-cat="trendar">
            Trendar
          </NavLink>
        </nav>
      </header>

      <main className="k2-container">{children}</main>

      <footer className="k2-footer">
        <span>
          © {new Date().getFullYear()} Korthet ·{" "}
          <Link to="/om">Om Korthet</Link> ·{" "}
          <Link to="/om-korthet/redaktionen">Redaktionen</Link> ·{" "}
          <Link to="/amnen">Ämnen</Link> · <a href="/rss.xml">RSS</a>
        </span>
        <span>
          AI-genererade sammanfattningar. Korthet ersätter inte originaljournalistiken –
          vi hjälper dig vidare till originalkällan.
        </span>

      </footer>
    </div>
  );
}